/**
 * The Macro notes — QueVee's short note on each Macro section and one across
 * them (`docs/plans/macro-commentary.md`).
 *
 * | | where | cost |
 * |---|---|---|
 * | read every kind's latest, or one written `at` | qv-data's admin `GET /macro/notes[/{kind}?at=]` | free |
 * | ↻ a new note for everyone | quevee's `POST /analyst/macro-notes/refresh` | one model call, charged to the presser |
 *
 * A read never throws: a failure is `null` notes and the block says so. ↻
 * returns `{ ok: false, error }` rather than throwing, `api/analyst.ts`'s rule.
 * The responses carry no model, no tokens and no user: the server leaves them out.
 */

import { authHeaders } from "../lib/session";
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(/\/$/, "");
const QUEVEE_URL = (import.meta.env.VITE_QUEVEE_URL ?? "").replace(/\/$/, "");

/** The kinds with a note: the five sections and the overall. Not the Calendar. */
export const NOTE_KINDS = ["macroReleases", "yieldCurve", "relationships", "havens", "positioning", "overall"] as const;
export type NoteKind = (typeof NOTE_KINDS)[number];

export function isNoteKind(kind: string): kind is NoteKind {
  return (NOTE_KINDS as readonly string[]).includes(kind);
}

/** The words, as the validator guarantees them. */
export interface MacroNoteBody {
  /** 3–5 sentences. */
  text: string;
  /** One sentence on when this state happened before and what followed, or null. */
  lastTime: string | null;
  /** The newest date in the numbers the note read. */
  asOf?: string | null;
  section?: string | null;
}

export interface MacroNote {
  kind: NoteKind;
  note: MacroNoteBody;
  writtenAt: string;
  builtAt: string;
  /** This is the newest note for its kind. */
  latest: boolean;
  /** Every note for the kind, newest first — the *As of* list. */
  history: { writtenAt: string; builtAt: string }[];
}

export type MacroNotes = Partial<Record<NoteKind, MacroNote | null>>;

/** Every kind's newest note; null when the read failed. Never throws. */
export async function fetchMacroNotes(): Promise<MacroNotes | null> {
  if (!BASE) return null;
  try {
    const res = await fetch(`${BASE}/macro/notes`, { headers: await requestHeaders() });
    if (!res.ok) return null;
    return ((await res.json()) as { notes?: MacroNotes }).notes ?? null;
  } catch {
    return null;
  }
}

/** One note — the one written `at`, from the *As of* list. Never throws. */
export async function fetchMacroNote(kind: NoteKind, at?: string): Promise<MacroNote | null> {
  if (!BASE) return null;
  const q = at ? `?at=${encodeURIComponent(at)}` : "";
  try {
    const res = await fetch(`${BASE}/macro/notes/${kind}${q}`, { headers: await requestHeaders() });
    return res.ok ? ((await res.json()) as MacroNote) : null;
  } catch {
    return null;
  }
}

export type RefreshResult =
  | { ok: true; note: MacroNoteBody; writtenAt: string; builtAt: string }
  | { ok: false; error: string; refreshAt?: string | null };

/** ↻: a new note on the newest numbers, for everyone. Never throws. */
export async function refreshMacroNote(kind: NoteKind): Promise<RefreshResult> {
  if (!QUEVEE_URL) return { ok: false, error: "The AI is not configured on this build." };
  try {
    const res = await fetch(`${QUEVEE_URL}/analyst/macro-notes/refresh`, {
      method: "POST",
      headers: { "content-type": "application/json", ...(await authHeaders()) },
      body: JSON.stringify({ kind }),
    });
    if (res.status === 403) return { ok: false, error: "Refreshing this note is not available on your account." };
    if (!res.ok) return { ok: false, error: `The note could not be refreshed (${res.status}).` };
    const j = (await res.json()) as
      | { ok: true; note: MacroNoteBody; writtenAt: string; builtAt: string }
      | { ok: false; error?: { message?: string }; refreshAt?: string };
    if (j.ok) return { ok: true, note: j.note, writtenAt: j.writtenAt, builtAt: j.builtAt };
    return { ok: false, error: j.error?.message ?? "The note could not be refreshed.", refreshAt: j.refreshAt ?? null };
  } catch {
    return { ok: false, error: "The note could not be refreshed." };
  }
}
